import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Award, MapPin, Trophy, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { INSTRUCTOR, LINEAGE, IMAGES } from "../mock";
import Seo, { PROFESSOR_JSONLD } from "../components/Seo";

const About = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="pt-24">
      <Seo
        title="About Professor Sergio Silva — 5th Degree BJJ Black Belt | Alameda BJJ"
        description="Meet Professor Sergio Silva: 5th Degree Brazilian Jiu-Jitsu Black Belt, Judo Black Belt and former Submission Wrestling World Champion, teaching martial arts since 1990 in Alameda, CA."
        path="/about"
        jsonLd={PROFESSOR_JSONLD}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]}
      />
      {/* header */}
      <section className="bg-[#F4F5F1] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Our Story</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-[#1A1A1A] max-w-3xl leading-tight">
            The Home of World Champion Sergio Silva
          </h1>
          <p className="mt-5 text-neutral-600 max-w-2xl leading-relaxed">
            Thanks for choosing our dojo for your personal development. We've been spreading the world of Jiu-Jitsu for over 35 years, developing 17+ black belts — from white belt all the way to black — right here in Alameda.
          </p>
        </div>
      </section>

      {/* bio */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <div className="reveal">
            <img src={INSTRUCTOR.image} alt={INSTRUCTOR.name} className="rounded-2xl w-full h-[520px] object-cover shadow-xl" />
          </div>
          <div className="reveal">
            <h2 className="font-display text-3xl font-bold text-[#1A1A1A]">{INSTRUCTOR.name}</h2>
            <p className="mt-2 text-br-blue font-semibold">{INSTRUCTOR.title}</p>
            {INSTRUCTOR.bio.map((p, i) => (
              <p key={i} className="mt-4 text-neutral-600 leading-relaxed">{p}</p>
            ))}
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Team Silva Brazilian Jiu-Jitsu started in 1997 in Curitiba, southern Brazil. Professor Silva has trained deeply across Wrestling, Judo, Muay Thai, Boxing and Brazilian Jiu-Jitsu, and moved to the United States in 1999 with a mission: to help others discover the world's most powerful martial art.
            </p>
            <div className="mt-6 flex items-center gap-2 text-neutral-500 text-sm">
              <MapPin size={16} className="text-br-green" /> Teaching Martial Arts since 1990 • Alameda since 2008
            </div>
          </div>
        </div>
      </section>

      {/* MMA coaching */}
      <section className="pb-8 sm:pb-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal rounded-2xl bg-[#F4F5F1] p-8 sm:p-12">
            <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">MMA Coaching & Gracie Legacy</span>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-[#1A1A1A]">Cornering World-Class Fighters Since 1997</h2>
            {INSTRUCTOR.mma.map((p, i) => (
              <p key={i} className="mt-4 text-neutral-600 leading-relaxed max-w-4xl">{p}</p>
            ))}
            <div className="mt-6 flex flex-wrap gap-2.5">
              {["BJ Penn", "Maurício \"Shogun\" Rua", "Murilo \"Ninja\" Rua", "Wanderlei Silva", "Ralph Gracie"].map((n) => (
                <span key={n} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-neutral-200 px-4 py-1.5 text-sm font-medium text-[#1A1A1A]">
                  <Trophy size={14} className="text-br-green" /> {n}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Law Enforcement & Military */}
      <section className="pb-8 sm:pb-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal rounded-2xl bg-[#0E1613] text-white p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 h-48 w-48 bg-br-green/20 rounded-full blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.2em] text-br-yellow uppercase">
                <Shield size={16} /> Law Enforcement & Military
              </span>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold">Training Police & Military Since 1997</h2>
              {INSTRUCTOR.tactical.map((p, i) => (
                <p key={i} className="mt-4 text-neutral-300 leading-relaxed max-w-4xl">{p}</p>
              ))}
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {INSTRUCTOR.tacticalHighlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-2.5 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <Shield size={18} className="text-br-yellow shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-200">{h}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* achievements */}
      <section className="section-pad bg-[#0E1613]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center reveal">
            <Trophy className="mx-auto text-br-yellow" size={34} />
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-white">Championship Titles</h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INSTRUCTOR.achievements.map((a, i) => (
              <div key={i} className="reveal bg-white/5 border border-white/10 rounded-xl p-6 flex items-start gap-3">
                <Award className="text-br-yellow shrink-0" size={22} />
                <span className="text-neutral-200">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* lineage */}
      <section className="section-pad">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center reveal">
            <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Lineage</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A]">A Lifetime of Learning</h2>
          </div>
          <div className="mt-12 relative">
            <div className="absolute left-3 sm:left-1/2 top-0 bottom-0 w-px bg-neutral-200" />
            <div className="space-y-8">
              {LINEAGE.map((l, i) => (
                <div key={i} className={`reveal relative sm:grid sm:grid-cols-2 sm:gap-8 items-center ${i % 2 ? "" : ""}`}>
                  <div className={`pl-10 sm:pl-0 ${i % 2 === 0 ? "sm:text-right sm:pr-10" : "sm:col-start-2 sm:pl-10"}`}>
                    <span className="inline-block text-xs font-bold text-white bg-br-green rounded-full px-3 py-1">{l.years}</span>
                    <p className="mt-2 text-neutral-700 font-medium">{l.school}</p>
                  </div>
                  <span className="absolute left-1.5 sm:left-1/2 sm:-translate-x-1/2 top-1 h-3 w-3 rounded-full bg-br-yellow ring-4 ring-white" />
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-14 reveal">
            <Link to="/programs"><Button className="bg-br-green hover:bg-[#00822f] text-white rounded-full px-8 h-12">Explore Our Programs</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
